/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Apr 17 2024
 **/

import { useEffect, useRef } from "react";
import { useAtomCallback } from "jotai/utils";
import { initializeApp } from "firebase/app";
import { OAuthProvider, getAuth } from "firebase/auth";
import { getMessaging } from "firebase/messaging";

import { checkVersion } from "@/frameworks/libraries/components";
import { Properties } from "@/frameworks/libraries/properties";
import { JotaiStore } from "@/frameworks/libraries/commons";

import firebaseDevConfig from "@/frameworks/core/firebase/firebase_dev.config.json";
import firebaseAlphaConfig from "@/frameworks/core/firebase/firebase_alpha.config.json";
import firebaseBetaConfig from "@/frameworks/core/firebase/firebase_beta.config.json";
import firebaseOperateConfig from "@/frameworks/core/firebase/firebase_operate.config.json";
import { FirebaseStateAtom, FirebaseState } from "./atom";
import FirebaseServiceController from "../../firebase/controller/FirebaseServiceController";

export default function FirebaseProvider({ children }: React.PropsWithChildren) {
	const set = useAtomCallback(
		(_, set, args: FirebaseState) => {
			set(FirebaseStateAtom, args);
			FirebaseServiceController.getInstance().setFirebaseAppState(args);
		},
		{
			store: JotaiStore
		}
	);

	const initFirebase = useRef(() => {
		const gvt = checkVersion({ allows: "gvt" });
		const productMode = Properties.productMode;
		const firebaseConfig =
			gvt.allowed || productMode === "dev"
				? firebaseDevConfig
				: productMode === "alpha"
					? firebaseAlphaConfig
					: productMode === "operate"
						? firebaseOperateConfig
						: firebaseBetaConfig;

		const firebaseApp = initializeApp(firebaseConfig);
		const auth = getAuth(firebaseApp);
		const messaging = getMessaging(firebaseApp);
		const appleProvider = new OAuthProvider("apple.com");
		const googleProvider = new OAuthProvider("google.com");

		googleProvider.setCustomParameters({
			prompt: "select_account"
		});

		return {
			googleProvider,
			appleProvider,
			auth,
			messaging,
			firebaseApp
		};
	});

	useEffect(() => {
		set(initFirebase.current());
	}, []);

	return <>{children}</>;
}
